import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, post } from '@yingbaba/api';
import type { MerchantOrganizationCandidate } from '@yingbaba/types';
import { Select, Button, ErrorNotice, Field, Modal, Notice, Status, useToast } from '@yingbaba/ui';
import { Plus, Search } from 'lucide-react';

export function PaymentMerchantDialog() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<MerchantOrganizationCandidate[]>([]);
  const [selectedID, setSelectedID] = useState('');
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const qc = useQueryClient();
  const toast = useToast();
  const selected = candidates.find((item) => item.organization_id === selectedID);

  async function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (query.trim().length < 2) return;
    setSearching(true);
    setError(null);
    try {
      const items = await api<MerchantOrganizationCandidate[]>(
        `/admin/merchant-organization-candidates?q=${encodeURIComponent(query.trim())}`,
      );
      setCandidates(items);
      setSelectedID('');
      setSearched(true);
    } catch (cause) {
      setError(cause);
    } finally {
      setSearching(false);
    }
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const values = Object.fromEntries(new FormData(event.currentTarget)) as Record<string, string>;
    setBusy(true);
    setError(null);
    try {
      await post('/admin/payment-merchants', {
        ...values,
        organization_id: selected.organization_id,
      });
      await qc.invalidateQueries();
      toast('支付商户配置已保存');
      setOpen(false);
      setSelectedID('');
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        <Plus size={15} />
        配置商户
      </Button>
      <Modal
        open={open}
        busy={busy}
        onOpenChange={setOpen}
        title="配置支付商户"
        description="先从已审核通过的服务商组织中选择，再填写渠道入驻信息。"
      >
        <form className="form-stack" onSubmit={search}>
          <Field
            label="搜索服务商组织"
            htmlFor="merchant-org-search"
            hint="输入组织名称、服务商名称或完整组织 ID，至少 2 个字符。"
          >
            <div className="row">
              <input
                id="merchant-org-search"
                className="input"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setCandidates([]);
                  setSelectedID('');
                  setSearched(false);
                }}
                minLength={2}
                required
              />
              <Button type="submit" variant="secondary" loading={searching}>
                <Search size={15} />
                搜索
              </Button>
            </div>
          </Field>
        </form>
        {searched && !candidates.length && (
          <Notice>没有找到已审核通过的服务商，请核对名称或先完成服务商审核。</Notice>
        )}
        {candidates.length > 0 && (
          <fieldset className="form-stack merchant-candidates">
            <legend>选择服务商组织</legend>
            {candidates.map((item) => (
              <label className="list-item" key={item.organization_id}>
                <input
                  type="radio"
                  name="merchant-organization"
                  value={item.organization_id}
                  checked={selectedID === item.organization_id}
                  onChange={() => setSelectedID(item.organization_id)}
                />
                <span className="grow">
                  <strong>{item.organization_name}</strong>
                  <span className="sub-cell">
                    服务商展示名：{item.provider_name} · ID：{item.organization_id}
                  </span>
                </span>
                <Status value={item.provider_status} label="已审核" />
              </label>
            ))}
          </fieldset>
        )}
        {selected && (
          <form className="form-stack" onSubmit={save}>
            <Notice>
              当前选择：{selected.organization_name}（{selected.organization_id}）
            </Notice>
            <Field label="支付渠道" htmlFor="merchant-provider">
              <Select id="merchant-provider" name="provider" required>
                <option value="wechat">微信支付</option>
                <option value="alipay">支付宝</option>
                <option value="unionpay">银联 / 银行卡</option>
              </Select>
            </Field>
            <Field label="二级商户 / 特约商户号" htmlFor="merchant-external-id">
              <input className="input" id="merchant-external-id" name="external_merchant_id" />
            </Field>
            <Field label="入驻状态" htmlFor="merchant-status">
              <Select id="merchant-status" name="status" defaultValue="pending">
                <option value="pending">申请中</option>
                <option value="active">已启用</option>
                <option value="disabled">已停用</option>
                <option value="rejected">未通过</option>
              </Select>
            </Field>
            <Field label="渠道入驻申请编号" htmlFor="merchant-reference">
              <input className="input" id="merchant-reference" name="onboarding_reference" />
            </Field>
            <Field
              label="已开通能力"
              htmlFor="merchant-capabilities"
              hint="仅记录渠道审核结果，不保存 API Key、私钥或证书。"
            >
              <input
                className="input"
                id="merchant-capabilities"
                name="capabilities"
                defaultValue="payment,split,refund"
                required
              />
            </Field>
            <ErrorNotice error={error} />
            <Button type="submit" loading={busy}>
              确认保存商户配置
            </Button>
          </form>
        )}
        {!selected && <ErrorNotice error={error} />}
      </Modal>
    </>
  );
}
